/**
 * The Statistics panel's figures for the open level, as pure functions: which
 * assets the level's components reference, what each costs (from the content
 * index's build-time stats), totals, and sorting.
 *
 * Everything here is knowable without the GPU: download size, triangles,
 * vertices, geometry bytes and texture sizes. Texture memory is an estimate
 * (RGBA8 with a full mip chain); real GPU memory isn't visible to a browser.
 */
import type { AssetIndexEntry, TextureSize } from "../../assets/content";
import type { EntityId, EntityRecord } from "../../world/entity";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import { assetLabel } from "../widgets/assetPickerModel";

export interface AssetRow {
  readonly path: string;
  readonly name: string;
  /** How many components reference it (it's loaded once regardless). */
  readonly uses: number;
  readonly downloadBytes: number;
  readonly triangles: number;
  readonly vertices: number;
  readonly geometryBytes: number;
  /** Estimated: RGBA8 plus mips, over its textures. */
  readonly textureBytes: number;
}

export type Totals = Pick<
  AssetRow,
  "downloadBytes" | "triangles" | "vertices" | "geometryBytes" | "textureBytes"
>;

export type SortKey = keyof Omit<AssetRow, "path">;
export type SortDirection = "ascending" | "descending";

/** Asset paths referenced by the entities' assetRef props, with how many times. Generic over schemas. */
export function assetsInUse(
  entities: ReadonlyMap<EntityId, EntityRecord>,
  registry: ComponentRegistry,
): Map<string, number> {
  const uses = new Map<string, number>();
  for (const entity of entities.values()) {
    for (const [type, data] of entity.components) {
      const props = registry.get(type)?.props ?? {};
      for (const [key, prop] of Object.entries(props)) {
        const value = data[key];
        if (prop.type === "assetRef" && typeof value === "string") {
          uses.set(value, (uses.get(value) ?? 0) + 1);
        }
      }
    }
  }
  return uses;
}

/**
 * A texture's estimated GPU memory, plus a third for the mip chain: 4 bytes a
 * pixel decoded to RGBA8, or 1 for KTX2, which transcodes to a block format
 * (BC3, ETC2 or ASTC 4×4 with alpha; half that without, so this errs high).
 */
export function textureMemory({ width, height, compressed }: TextureSize): number {
  const bytesPerPixel = compressed ? 1 : 4;
  return Math.round((width * height * bytesPerPixel * 4) / 3);
}

/**
 * One row per asset in use that the catalog knows, in catalog order. Engine
 * shapes are built in, not in the catalog, so they cost nothing and get no row.
 */
export function statisticsRows(
  catalog: readonly AssetIndexEntry[],
  inUse: ReadonlyMap<string, number>,
): AssetRow[] {
  return catalog.flatMap((asset) => {
    const uses = inUse.get(asset.path);
    if (uses === undefined) return [];
    const stats = asset.stats;
    return [
      {
        path: asset.path,
        name: assetLabel(asset.path),
        uses,
        downloadBytes: asset.bytes,
        triangles: stats?.triangles ?? 0,
        vertices: stats?.vertices ?? 0,
        geometryBytes: stats?.geometryBytes ?? 0,
        textureBytes: (stats?.textures ?? []).reduce((sum, t) => sum + textureMemory(t), 0),
      },
    ];
  });
}

export function totalsOf(rows: readonly AssetRow[]): Totals {
  const sum = (key: keyof Totals) => rows.reduce((total, row) => total + row[key], 0);
  return {
    downloadBytes: sum("downloadBytes"),
    triangles: sum("triangles"),
    vertices: sum("vertices"),
    geometryBytes: sum("geometryBytes"),
    textureBytes: sum("textureBytes"),
  };
}

export function sortRows(
  rows: readonly AssetRow[],
  key: SortKey,
  direction: SortDirection,
): AssetRow[] {
  const sign = direction === "ascending" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const [x, y] = [a[key], b[key]];
    const order = typeof x === "string" ? x.localeCompare(String(y)) : x - Number(y);
    return sign * order;
  });
}
